import React, { useMemo } from "react";
import { Button } from "@/ui/components/ui/button";
import { Calendar, RotateCw, Timer, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/components/ui/tooltip";
import { Separator } from "@/ui/components/ui/separator";
import { AlertsBell } from "@/alerts/components/alerts-bell";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore";
import { SHIFT_DEFINITIONS } from "@/shared/utils/shifts";
import { formatOrgTime, useOrgTimeZone } from "@/shared/utils/date/org-time";
import { addZonedDays } from "@/shared/utils/date/zoned-wall-clock";
import classNames from "classnames";
import { useTopBarHandlers } from "./use-top-bar-handlers";
import { useBreadcrumbs, type BreadcrumbEntry } from "@/shared/contexts/breadcrumb-context";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/ui/components/ui/breadcrumb.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu";
import { Skeleton } from "@/ui/components/ui/skeleton";
import { useGetOrganizations } from "@/auth/api/queries/user.queries";
import { useGetAllLocations } from "@/locations/api/queries/location.queries";
import { useGetMachinesByLocation } from "@/machines/api/queries/machines.queries";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { SidebarTrigger } from "@/ui/components/ui/sidebar";

/** `crumb.type` is a wire word; its sr-only label is not. */
const SWITCH_LABEL_KEY = {
  organizations: "topBar.switchOrganizations",
  locations: "topBar.switchLocations",
  machines: "topBar.switchMachines",
} as const;

const TopBar = () => {
  const { t } = useTranslation("common");
  // Custom hook for all handlers
  const {
    handleDateTimeClick,
    handleAutoRefreshClick,
    handleRefreshClick,
    isLoading,
  } = useTopBarHandlers();

  // Date time range state
  const { value, setValue, selectedPresetKey, compareToOption, targetSourceOption } = useDateTimeRangeStore();
  // The range is a wall-clock window at the active LOCATION — label it in that
  // zone, and name the zone so the basis is never ambiguous.
  const timeZone = useOrgTimeZone();
  const timeZoneLabel = useMemo(() => timeZone.split("/").pop()?.replace(/_/g, " ") ?? timeZone, [timeZone]);

  // Calculate display label for selected date range
  const dateRangeLabel = useMemo(() => {
    const { fromDate, toDate } = value;

    // If a preset is selected, show the preset label
    if (selectedPresetKey) {
      // Named shift presets (SHIFT - NIGHT, etc.) resolve to their shift label.
      const shift = SHIFT_DEFINITIONS.find((s) => s.key === selectedPresetKey);
      if (shift) return t(shift.labelKey);
      switch (selectedPresetKey) {
        case "last15min":
          return t("dateRange.presets.last15min");
        case "last30min":
          return t("dateRange.presets.last30min");
        case "last1hour":
          return t("dateRange.presets.last1hour");
        case "last3hours":
          return t("dateRange.presets.last3hours");
        case "last12hours":
          return t("dateRange.presets.last12hours");
        case "last24hours":
          return t("dateRange.presets.last24hours");
        case "custom":
          // For custom, show formatted range
          break;
        default:
          break;
      }
    }

    // For custom or no preset, show formatted range — in the location's zone,
    // matching the zone the picker built it in.
    const isSameDay =
      formatOrgTime(fromDate, "YYYY-MM-DD", timeZone) === formatOrgTime(toDate, "YYYY-MM-DD", timeZone);
    if (isSameDay) {
      return `${formatOrgTime(fromDate, "MMM D, HH:mm", timeZone)} - ${formatOrgTime(toDate, "HH:mm", timeZone)}`;
    } else {
      return `${formatOrgTime(fromDate, "MMM D, HH:mm", timeZone)} - ${formatOrgTime(toDate, "MMM D, HH:mm", timeZone)}`;
    }
  }, [value, selectedPresetKey, timeZone, t]);

  // Format compare to label
  const compareToLabel = useMemo(() => {
    if (!compareToOption) return null;
    switch (compareToOption) {
      case "previous-shift":
        return t("dateRange.summary.comparePreviousShift");
      case "previous-day":
        return t("dateRange.summary.comparePreviousDay");
      case "avg-last-7-days":
        return t("dateRange.summary.compareAvgLast7Days");
      case "previous-hour":
        return t("dateRange.summary.comparePreviousHour");
      default:
        return null;
    }
  }, [compareToOption, t]);

  // Format target source label
  const targetSourceLabel = useMemo(() => {
    if (!targetSourceOption) return null;
    switch (targetSourceOption) {
      case "plan":
        return t("dateRange.summary.targetPlan");
      case "historical-avg":
        return t("dateRange.summary.targetHistoricalAvg");
      case "machine-targets":
        return t("dateRange.summary.targetMachineTargets");
      default:
        return null;
    }
  }, [targetSourceOption, t]);

  const { breadcrumbs } = useBreadcrumbs();

  return (
    <div className="flex h-14 items-center justify-between gap-2 border-b px-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Sidebar trigger and Breadcrumbs - Left Side */}
      <div className="flex items-center gap-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />

        {breadcrumbs.length > 0 && (
          <>
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => {
                  const isLast = index === breadcrumbs.length - 1;

                  return (
                    <React.Fragment key={index}>
                      <BreadcrumbItem className="flex items-center gap-1">
                        {crumb.isLoading ? (
                          <>
                            <Skeleton className="h-4 w-24" />
                            {crumb.type && <Skeleton className="h-3 w-3 rounded-sm" />}
                          </>
                        ) : isLast ? (
                          <>
                            <BreadcrumbPage>{crumb.breadcrumb}</BreadcrumbPage>
                            {crumb.type && <BreadcrumbDropdown crumb={crumb} />}
                          </>
                        ) : (
                          <>
                            <BreadcrumbLink asChild>
                              <Link to={crumb.path}>{crumb.breadcrumb}</Link>
                            </BreadcrumbLink>
                            {crumb.type && <BreadcrumbDropdown crumb={crumb} />}
                          </>
                        )}
                      </BreadcrumbItem>
                      {!isLast && <BreadcrumbSeparator />}
                    </React.Fragment>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          </>
        )}
      </div>

      {/* Action Icons - Right Side */}
      <div className="flex items-center gap-2">
        <TooltipProvider>
          {/* Refresh Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={handleRefreshClick}
                disabled={isLoading}
              >
                <RotateCw className={classNames("h-4 w-4", { "animate-spin": isLoading })} />
                <span className="sr-only">{t("topBar.refresh")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isLoading ? t("topBar.refreshing") : t("topBar.refresh")}</p>
            </TooltipContent>
          </Tooltip>

          {/* Auto-Refresh Settings Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={handleAutoRefreshClick}
              >
                <Timer className="h-4 w-4" />
                <span className="sr-only">{t("topBar.autoRefresh")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("topBar.autoRefresh")}</p>
            </TooltipContent>
          </Tooltip>

          {/* Prev/Next Day (debug) */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={() =>
                  setValue({
                    fromDate: addZonedDays(value.fromDate, -1, timeZone),
                    toDate: addZonedDays(value.toDate, -1, timeZone),
                  })
                }
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="sr-only">{t("topBar.previousDay")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent><p>{t("topBar.previousDay")}</p></TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={() =>
                  setValue({
                    fromDate: addZonedDays(value.fromDate, 1, timeZone),
                    toDate: addZonedDays(value.toDate, 1, timeZone),
                  })
                }
              >
                <ChevronRight className="h-4 w-4" />
                <span className="sr-only">{t("topBar.nextDay")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent><p>{t("topBar.nextDay")}</p></TooltipContent>
          </Tooltip>

          {/* Date/Calendar Button - Open Modal with DateTimeRangePicker */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 gap-2 px-3"
                onClick={handleDateTimeClick}
              >
                <Calendar className="h-4 w-4" />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{dateRangeLabel}</span>
                  <span className="text-xs text-muted-foreground">{timeZoneLabel}</span>
                  {(compareToLabel || targetSourceLabel) && (
                    <>
                      <Separator orientation="vertical" className="h-4" />
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {compareToLabel && <span>{compareToLabel}</span>}
                        {compareToLabel && targetSourceLabel && (
                          <Separator orientation="vertical" className="h-3" />
                        )}
                        {targetSourceLabel && <span>{targetSourceLabel}</span>}
                      </div>
                    </>
                  )}
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("topBar.selectDateRange")}</p>
            </TooltipContent>
          </Tooltip>

          {/* Notifications bell — popover of recent alerts + unseen badge */}
          <AlertsBell />
        </TooltipProvider>
      </div>
    </div>
  );
};

// Dropdown component for breadcrumb items with type
const BreadcrumbDropdown = ({ crumb }: { crumb: BreadcrumbEntry }) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { setSelectedOrganizationId } = useOrganizationStore();

  const { data: orgsData } = useGetOrganizations();
  const { data: locationsData } = useGetAllLocations();
  const { data: machinesData } = useGetMachinesByLocation(crumb.locationId);

  let items: Array<{ id: number; name: string; path: string }> = [];

  if (crumb.type === "organizations" && orgsData?.data) {
    items = orgsData.data.map(org => ({
      id: org.id,
      name: org.name,
      path: crumb.path, // Organizations always navigate to same base path
    }));
  } else if (crumb.type === "locations" && locationsData?.data) {
    items = locationsData.data.map(loc => ({
      id: loc.id,
      name: loc.name,
      path: crumb.path.replace(/\/\d+$/, `/${loc.id}`), // Replace ID in path
    }));
  } else if (crumb.type === "machines" && machinesData?.data) {
    items = machinesData.data.map(machine => ({
      id: machine.id,
      name: machine.name,
      path: crumb.path.replace(/\/\d+$/, `/${machine.id}`), // Replace ID in path
    }));
  }

  // Don't show dropdown if only one item or no items
  if (items.length <= 1) return null;

  const handleSelect = (item: typeof items[0]) => {
    // In-page subject (e.g. the location on `/map`): the owning page applies the
    // pick to its own state and the URL doesn't move.
    if (crumb.onSelect) {
      crumb.onSelect(item.id);
      return;
    }
    if (crumb.type === "organizations") {
      setSelectedOrganizationId(item.id);
    }
    navigate(item.path);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 hover:bg-transparent">
          <ChevronDown className="h-3 w-3" />
          <span className="sr-only">{crumb.type ? t(SWITCH_LABEL_KEY[crumb.type]) : ""}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {items.map((item) => (
          <DropdownMenuItem
            key={item.id}
            onClick={() => handleSelect(item)}
            className={crumb.currentId === item.id ? "bg-accent" : ""}
          >
            {item.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TopBar;

